import { find } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import Loader from '../../../core/components/Loader';
import { useGetReportQuery } from '../../../core/api/rtk/analyticsApi';
import { useAppContext } from '../../../providers/AppProvider';
import { selectSite } from '../../../store/authReducer';
import { selectSites } from '../../site-selection/reducer';
import NavTabs from '../components/NavTabs';
import Report from '../components/ReportFrame';
import { setFilter } from '../helpers';
import { useEmbedTokenRefresh } from '../useEmbedTokenRefresh';

const AnalyticsReport = ({ reportKey, reportName, actions }) => {
  const intl = useIntl();
  const { isWashClub } = useAppContext();
  const currentSite = useSelector(selectSite);
  const sites = useSelector(selectSites);

  const reportRef = useRef(null);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(null);

  const { data, isLoading, isError, refetch } = useGetReportQuery(
    { specificReport: reportKey, siteId: currentSite },
    { skip: !reportKey || !currentSite }
  );

  useEmbedTokenRefresh(data?.embedToken?.expiration, refetch);

  useEffect(() => {
    if (data?.pages?.length) {
      setCurrentPage(data.pages[0].name);
    }
  }, [data]);

  useEffect(() => {
    if (reportRef.current && currentPage) {
      reportRef.current.setPage(currentPage);
    }
  }, [currentPage]);

  const applyFilter = useCallback(() => {
    if (!reportRef.current) return;
    const currentSelectedSite = find(sites, (s) => s.id === Number(currentSite));
    if (!currentSelectedSite) return;
    const conditions = [
      {
        operator: 'Is',
        value: isWashClub ? currentSelectedSite.extId : currentSelectedSite.externalId,
      },
    ];
    reportRef.current.setFilters([setFilter(conditions, isWashClub)]);
  }, [sites, currentSite, isWashClub]);

  const handleReport = useCallback(
    (report) => {
      if (!reportRef.current) {
        if (!report) {
          setError(true);
          return;
        }
        reportRef.current = report;
      }
      if (report?.iframe?.contentWindow) {
        applyFilter();
      }
    },
    [applyFilter]
  );

  useEffect(() => {
    if (reportRef.current) applyFilter();
  }, [applyFilter]);

  const hasTabs = data?.pages?.length > 1;

  return (
    <div className="container-fluid">
      <section className="page-main-content powerbi-report">
        {(reportName || actions || hasTabs) && (
          <div className="powerbi-report-header">
            {(reportName || actions) && (
              <div className="powerbi-report-header-row">
                <span className="powerbi-report-title">{reportName}</span>
                <div className="powerbi-report-actions">{actions}</div>
              </div>
            )}
            {hasTabs && (
              <div className="powerbi-tab-container">
                <div className="powerbi-tab-scroll">
                  <NavTabs
                    pages={data.pages}
                    currentPage={currentPage}
                    className="analytics-tabs-new"
                    setCurrentPage={(name) => {
                      setCurrentPage(name);
                      setError(false);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {isLoading && <Loader />}
        {(isError || error) && (
          <div className="powerbi-error mt-3">
            {intl.formatMessage({ id: 'powerbiError' }, { error: 'Error loading report' })}
          </div>
        )}
        {data && !isLoading && !isError && !error && (
          <Report
            accessToken={data.embedToken.token}
            embedUrl={data.embedUrl}
            embedId={data.id}
            onLoad={handleReport}
          />
        )}
      </section>
    </div>
  );
};

export default AnalyticsReport;
